import React, { useState, useEffect } from "react";

/**
 * Props:
 * - mode: "create" | "edit"
 * - initialData?: VideoDto
 * - onSubmit(formData: VideoDto): Promise<void> | void
 * - submitting?: boolean
 */
export default function VideoForm({ mode = "create", initialData, onSubmit, submitting }) {
  const [formData, setFormData] = useState({
    id: 0,
    title: "",
    description: "",
    videoUrl: "",
    thumbNailUrl: "",
    uplodedBy: "",
    uplodedDate: "", // "YYYY-MM-DD"
    isActive: false,
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        id: initialData.id ?? 0,
        title: initialData.title ?? "",
        description: initialData.description ?? "",
        videoUrl: initialData.videoUrl ?? "",
        thumbNailUrl: initialData.thumbNailUrl ?? "",
        uplodedBy: initialData.uplodedBy ?? "",
        // keep only YYYY-MM-DD for <input type="date" />
        uplodedDate: initialData.uplodedDate ? String(initialData.uplodedDate).slice(0, 10) : "",
        isActive: Boolean(initialData.isActive),
      });
    }
  }, [initialData]);

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    // all fields are nullable in schema, but basic UX checks help
    if (formData.videoUrl && !/^https?:\/\//i.test(formData.videoUrl)) {
      e.videoUrl = "Must be a valid URL starting with http or https.";
    }
    if (formData.thumbNailUrl && !/^https?:\/\//i.test(formData.thumbNailUrl)) {
      e.thumbNailUrl = "Must be a valid URL starting with http or https.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit?.(formData);
  };

  return (
    <div className="card p-4 shadow-sm">
      <h5 className="mb-3">{mode === "edit" ? "Edit Video" : "Add New Video"}</h5>

      <form onSubmit={submit} className="row g-3">
        {mode === "edit" && (
          <div className="col-md-2">
            <label className="form-label">ID</label>
            <input className="form-control" name="id" value={formData.id} readOnly />
          </div>
        )}

        <div className={mode === "edit" ? "col-md-10" : "col-12"}>
          <label className="form-label">Title</label>
          <input
            type="text"
            name="title"
            className="form-control"
            value={formData.title}
            onChange={handleChange}
            placeholder="Optional title"
          />
        </div>

        <div className="col-12">
          <label className="form-label">Description</label>
          <textarea
            name="description"
            className="form-control"
            rows={3}
            value={formData.description}
            onChange={handleChange}
            placeholder="Optional description"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Video URL</label>
          <input
            type="url"
            name="videoUrl"
            className={`form-control ${errors.videoUrl ? "is-invalid" : ""}`}
            value={formData.videoUrl}
            onChange={handleChange}
            placeholder="https://..."
          />
          {errors.videoUrl && <div className="invalid-feedback">{errors.videoUrl}</div>}
        </div>

        <div className="col-md-6">
          <label className="form-label">Thumbnail URL</label>
          <input
            type="url"
            name="thumbNailUrl"
            className={`form-control ${errors.thumbNailUrl ? "is-invalid" : ""}`}
            value={formData.thumbNailUrl}
            onChange={handleChange}
            placeholder="https://..."
          />
          {errors.thumbNailUrl && <div className="invalid-feedback">{errors.thumbNailUrl}</div>}
        </div>

        <div className="col-md-6">
          <label className="form-label">Uploaded By</label>
          <input
            type="text"
            name="uplodedBy"
            className="form-control"
            value={formData.uplodedBy}
            onChange={handleChange}
            placeholder="Name (optional)"
          />
        </div>

        <div className="col-md-6">
          <label className="form-label">Uploaded Date</label>
          <input
            type="date"
            name="uplodedDate"
            className="form-control"
            value={formData.uplodedDate}
            onChange={handleChange}
          />
        </div>

        <div className="col-12 form-check">
          <input
            className="form-check-input"
            type="checkbox"
            name="isActive"
            id="isActive"
            checked={formData.isActive}
            onChange={handleChange}
          />
          <label className="form-check-label" htmlFor="isActive">Is Active</label>
        </div>

        <div className="col-12 d-flex gap-2">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? "Saving..." : mode === "edit" ? "Update Video" : "Save Video"}
          </button>
        </div>
      </form>
    </div>
  );
}
